import { Badge, FlexContainer } from '../../components';
import { clsxm } from '../../utils';
import { createColumnHelper } from '@tanstack/react-table';

import TableColumnHeader from '../TableColumnHeader';

import type { BadgeColumnOptions, ColumnFactory } from './types';

/**
 * Factory function to create a badge column with variant and label mapping
 *
 * @example
 * ```tsx
 * // Legacy mapping approach
 * const STATUS_COLUMN = createBadgeColumn<Customer>({
 *   accessor: 'status',
 *   label: 'Status',
 *   options: {
 *     variantMapping: { active: 'positive', pending: 'warning' },
 *     labelMapping: { active: 'Active', pending: 'Pending' },
 *     renderExtra: (value, row) => row.hasIssues ? <Icon /> : null
 *   }
 * });
 *
 * // Modern function-based approach (recommended with status mappers)
 * const STATUS_COLUMN = createBadgeColumn<Customer>({
 *   accessor: 'status',
 *   label: 'Status',
 *   options: {
 *     getVariant: (status) => customerStatusMapper.getVariant(status),
 *     getLabel: (status) => customerStatusMapper.getLabel(status),
 *   }
 * });
 * ```
 */
export function createBadgeColumn<TData>(config: {
  accessor: keyof TData & string;
  label: string;
  options: BadgeColumnOptions<TData, TData[keyof TData]>;
}): ColumnFactory<TData> {
  const { accessor, label, options } = config;
  const { size = 150, variantMapping, labelMapping, getVariant, getLabel, renderExtra, className, meta } = options;

  const columnHelper = createColumnHelper<TData>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return columnHelper.accessor(accessor as any, {
    meta,
    size,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    header: ({ table }: { table: any }) => (
      <TableColumnHeader accessor={accessor} label={label} tableState={table.getState()} />
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => {
      const value = info.getValue();
      const row = info.row.original as TData;

      // Use function-based getters if provided, otherwise fall back to mappings
      const variant = getVariant ? getVariant(value) : variantMapping?.[value as string] || 'default';
      const displayLabel = getLabel ? getLabel(value) : labelMapping?.[value as string] || String(value);

      return (
        <FlexContainer className={clsxm('gap-2', className)} items="center" justify="start">
          <Badge variant={variant}>{displayLabel}</Badge>
          {renderExtra?.(value as TData[keyof TData], row)}
        </FlexContainer>
      );
    },
  });
}
